import React, {
	memo,
	useCallback,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	useHistory,
	useLocation
} from 'react-router';
import {
	Form,
	Header,
	// Icon,
	Message,
	Tab
} from 'semantic-ui-react';

import {
	log,
	print
} from '../../../../../services/utils';

import { useAuthState } from '../../../../../contexts/AuthContext';
import {
	useCalendarState,
	useCalendarAction
} from '../../../../../contexts/CalendarContext';

import Actions from '../../../../Shared/Actions';
import FormDatePicker from '../../../../Form/FormDatePicker';
import FormInput from '../../../../Form/FormInput';
import FormTextArea from '../../../../Form/FormTextArea';
import TagInput from '../../../../Shared/TagInput';

import styles from '../calendar.module.css';
import FormErrors from '../../../../Shared/FormErrors';

const InfoTab = ( {
	errors,
	forceValidation,
	formData,
	formDataHasChanged,
	handleSave,
	locks,
	resetFormData,
	updateField,
	validate
} ) => {
	const history = useHistory();
	const { state: navigationState } = useLocation();

	const { user: { permissions } } = useAuthState();
	const { eventTags } = useCalendarState();
	const { setEventTags } = useCalendarAction();

	const [ editable, setEditable ] = useState( !formData.id );
	const [ isSaving, setIsSaving ] = useState( false );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const save = useCallback( async () => {
		const cb = () => {
			if ( !validate() ) {
				setEditable( false );
			}
		};

		setIsSaving( true );
		await handleSave( cb );
		setIsSaving( false );
	}, [
		handleSave,
		validate
	] );

	const saveAction = useMemo(
		() => ( {
			action: save,
			disable: !formDataHasChanged || isSaving,
			icon: 'save',
			permission: true,
			text: formData.id === 0
				? 'Save As New Event'
				: 'Save'
		} ),
		[
			formData.id,
			formDataHasChanged,
			isSaving,
			save
		]
	);

	const cancel = useCallback( () => {
		resetFormData();

		if ( formData.id === 0 ) {
			if ( navigationState?.to ) {
				history.push( navigationState.to );
			} else {
				history.push( '/operations/calendar' );
			}
		} else {
			setEditable( false );
		}
	}, [
		formData.id,
		history,
		navigationState,
		resetFormData
	] );

	const cancelAction = useMemo(
		() => ( {
			action: cancel,
			icon: 'times',
			permission: true,
			text: 'Cancel'
		} ),
		[ cancel ]
	);

	const edit = useCallback( () => {
		setEditable( true );
	}, [] );

	const editAction = useMemo( () => ( {
		action: edit,
		disable: !permissions.editCalendarEvents.isAvailable,
		icon: 'pencil',
		permission: permissions.editCalendarEvents.isAvailable,
		text: 'Edit'
	} ), [
		edit,
		permissions
	] );

	const printAction = useMemo( () => ( {
		action: print,
		disable: validate(),
		icon: 'print',
		permission: permissions.viewCalendarEvents.canExecute,
		text: 'Print'
	} ), [
		permissions,
		validate
	] );

	const actions = useMemo(
		() => editable
			? [
				cancelAction,
				saveAction
			]
			: [
				printAction,
				editAction
			],
		[
			cancelAction,
			editable,
			saveAction,
			printAction,
			editAction
		]
	);

	// tags
	const addNewTag = useCallback( async tag => {
		await setEventTags();
		updateField( {
			target: {
				name: 'tags',
				value: [
					...formData.tags,
					tag.id
				]
			}
		} );
	}, [
		formData.tags,
		setEventTags,
		updateField
	] );

	// const mapIcon = useMemo(
	// 	() => !errors.email.some( error => error.error )
	// 		? <Icon
	// 			color="black"
	// 			link
	// 			name="map pin"
	// 			onClick={ () => console.log( 'show map' ) }
	// 		/>
	// 		: null
	// 	, [ errors.email ]
	// );

	const areSameDay = useCallback( ( start, end ) => new Date( start ).getDate() === new Date( end ).getDate(), [] );

	const formatTime = useCallback( ( time, tz ) => new Date( time )
		.toLocaleString(
			'en-US',
			{
				weekday: 'long',
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: 'numeric',
				minute: 'numeric',
				...tz && { timeZoneName: 'short' }
			}
		)
		.replace( ' at', ',' )
		.replace( 'AM', 'am' )
		.replace( 'PM', 'pm' ), [] );

	return (
		<Tab.Pane as="div">
			{ log( 'infoTab' ) }
			<Actions actions={ actions } />
			<div className="contact-form-container">
				<Form
					autoComplete="off"
					error={ forceValidation && validate() }
				>
					{ editable
						? <Form.Group>
							<FormInput
								disabled={ locks.includes( 'title' ) }
								errors={ errors.title }
								fieldLabel="Title"
								forceValidation={ forceValidation }
								maxLength={ 255 }
								name="title"
								onChange={ updateField }
								required
								readOnly={ !editable }
								value={ formData.title || '' }
								width={ 6 }
							/>
						</Form.Group>
						: <>
							<Header className={ styles.readonlySubheader }>
								{ new Date( formData.start ).toLocaleDateString(
									'en-US',
									{
										weekday: 'long',
										month: 'long',
										day: 'numeric'
									}
								)}
							</Header>
							<Header className={ styles.readonlyHeader }>
								{ formData.title }
							</Header>
						</>
					}

					<Form.Group
						className={
							editable
								? null
								: styles.readonlyField
						}
					>
						{
							editable
								? <>
									<FormDatePicker
										disabled={ locks.includes( 'start' ) }
										errors={ errors.start }
										fieldLabel="Start Time"
										forceValidation={ forceValidation }
										maxLength={ 255 }
										name="start"
										onChange={ updateField }
										required
										time
										value={ formData.start || '' }
										width={ 5 }
									/>
									<FormDatePicker
										disabled={ locks.includes( 'end' ) }
										errors={ errors.end }
										fieldLabel="End Time"
										forceValidation={ forceValidation }
										maxLength={ 255 }
										name="end"
										onChange={ updateField }
										required
										time
										value={ formData.end || '' }
										width={ 5 }
									/>
								</>
								: <>
									<FormInput
										fieldLabel={ <Header size="medium">Date and Time</Header>}
										name="readonlyDateAndTime"
										readOnly
										required
										value={ formData.end === '0001-01-01T00:00:00Z'
											? formatTime( formData.start, true )
											: areSameDay( formData.start, formData.end )
												? `${ formatTime( formData.start, false ) } - ${ new Date( formData.end )
													.toLocaleTimeString(
														'en-US',
														{
															hour: 'numeric',
															minute: 'numeric',
															timeZoneName: 'short'
														}
													)
													.replace( 'AM', 'am' )
													.replace( 'PM', 'pm' ) }`
												: `${ formatTime( formData.start, false ) } - ${ formatTime( formData.end, true ) }`
										}
										width={ 16 }
									/>
								</>
						}
					</Form.Group>

					<Form.Group
						className={ editable
							? null
							: styles.readonlyField
						}
					>
						{ editable
							? null
							: formData.isOnlineMeeting
								? <FormInput
									fieldLabel={ <Header size="medium">Location</Header> }
									name="readonlyLocation"
									readOnly
									required
									value={ formData.onlineMeetingUrl
										? <a
											href={ formData.onlineMeetingUrl }
											target="_blank"
											rel="noopener noreferrer"
										>
											{ formData.onlineMeetingProvider | 'Online Meeting' }
										</a>
										: formData.onlineMeetingProvider | 'Online Meeting'
									}
									width={ 16 }
								/>
								: <FormTextArea
									fieldLabel={ <Header size="medium">Location</Header> }
									name="location"
									readOnly
									value={ formData.location || '' }
									width={ 16 }
								/>
						}
					</Form.Group>

					<Form.Group
						className={ editable
							? null
							: styles.readonlyField
						}
					>
						<Form.Field>
							<label><Header size="medium">Description</Header></label>
							<div
								dangerouslySetInnerHTML={ { __html: formData.description } }
								style={ { marginBottom: '1rem' } }
							/>
						</Form.Field>
						{/* <FormTextArea
							disabled={ locks.includes( 'description' ) }
							fieldLabel={ <Header size="medium">Description</Header> }
							name="description"
							onChange={ updateField }
							readOnly={ !editable }
							rows={ 6 }
							value={ formData.description || '' }
							width={ 16 }
						/> */}
					</Form.Group>

					<Form.Group
						className={editable
							? null
							: styles.readonlyField
						}
					>
						<TagInput
							addNewTag={ addNewTag }
							availableTags={ eventTags }
							disabled={ locks.includes( 'tags' ) }
							fieldLabel={ <Header size="medium">Tags</Header> }
							handleUpdate={ updateField }
							tags={ formData.tags || [] }
							readOnly={ !editable }
						/>
					</Form.Group>
					<FormErrors mapFormErrors={ mapFormErrors } />
				</Form>
			</div>
		</Tab.Pane>
	);
};

InfoTab.propTypes = {
	errors: PropTypes.object,
	forceValidation: PropTypes.bool,
	formData: PropTypes.object,
	formDataHasChanged: PropTypes.bool,
	handleSave: PropTypes.func,
	locks: PropTypes.array,
	resetFormData: PropTypes.func,
	updateField: PropTypes.func,
	validate: PropTypes.func
};

export default memo( InfoTab );
